import { ImagesSkusChevronDownSvg, ImagesSkusSearchSvg } from '@/assets/images_skus.assets';
import DefaultButton from '@/components/common/default/DefaultButton';
import { SkusMobileSearchRefinementListProps } from '@/types/props/components/skus';
import { Checkbox, Field, Label, Transition } from '@headlessui/react';
import Image from '@/components/common/SpiritoryImage';
import { Fragment, useEffect, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { useRefinementList, useHits } from 'react-instantsearch';
import useEvents from '@/hooks/useEvents';
import type { SkuDTO } from '@/types/api/sku';

export default function SkusMobileSearchRefinementList({
  title,
  attributeName,
}: SkusMobileSearchRefinementListProps) {
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [filteredItems, setFilteredItems] = useState<typeof items>([]);

  const [showFilters, setShowFilters] = useState(false);

  const { items, refine } = useRefinementList({
    attribute: attributeName,
  });

  const { items: hitsItems } = useHits<SkuDTO>();
  const { fireSearchFilterFilledEvent } = useEvents('', hitsItems);

  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  useEffect(() => {
    setShowSearch(
      Boolean(
        attributeName === 'brand.name' ||
          attributeName === 'brand.region' ||
          attributeName === 'brand.countryOfOrigin'
      )
    );
  }, [attributeName]);

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    const searchValue = e.target.value.toLowerCase();
    const filteredItems = items.filter(item => item.label.toLowerCase().includes(searchValue));
    setFilteredItems(filteredItems);
  }

  return (
    <li>
      <DefaultButton
        className="tw-flex tw-w-full tw-items-center tw-justify-between tw-font-plusJakartaSans !tw-text-base tw-font-semibold tw-text-textBlack"
        onClick={() => setShowFilters(prev => !prev)}
      >
        {title}
        <Image
          src={ImagesSkusChevronDownSvg}
          width={5.59}
          height={12}
          alt="Chevron Down"
          className={`tw-transition-transform tw-duration-300 ${
            showFilters ? 'tw-rotate-180' : ''
          }`}
        />
      </DefaultButton>

      <Transition
        as={Fragment}
        show={showFilters}
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="tw-mt-4 tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 !tw-border-solid tw-border-custom-gray tw-pb-4">
          {showSearch && (
            <div className="tw-relative tw-mb-4">
              <Image
                src={ImagesSkusSearchSvg}
                width={18.333332061767578}
                height={18.333332061767578}
                alt="Search"
                className="tw-absolute tw-left-2 tw-top-1/2 -tw-translate-y-1/2"
                aria-hidden="true"
              />
              <input
                type="text"
                name="search"
                id="search"
                placeholder={`Search ${title}`}
                className="tw-w-full tw-rounded-md tw-border !tw-border-solid tw-border-custom-gray tw-bg-white tw-py-2 tw-pl-8 tw-pr-4 tw-text-base tw-font-medium tw-text-textBlack"
                onChange={handleSearch}
              />
            </div>
          )}
          <div className="tw-mb-4">
            <ul className="tw-space-y-2 tw-p-0">
              {filteredItems.map(item => (
                <Field as="li" className="tw-flex tw-items-center tw-gap-2" key={item.value}>
                  <Checkbox
                    id={`checkbox-country-${item.label}`}
                    name={`checkbox-country-${item.label}`}
                    checked={item.isRefined}
                    onChange={event => {
                      if (!item.isRefined) {
                        fireSearchFilterFilledEvent();
                      }
                      refine(item.value);
                    }}
                    className="tw-group tw-flex tw-size-6 tw-items-center tw-justify-center tw-rounded-md tw-border-2 !tw-border-solid tw-border-custom-gray tw-bg-white data-[checked]:tw-bg-main"
                  >
                    <FaCheck className="tw-text-xs tw-text-transparent group-data-[checked]:tw-text-white" />
                  </Checkbox>
                  <Label
                    className="tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-text-text"
                    htmlFor={`checkbox-country-${item.label}`}
                  >
                    {/* `bottleSizeInMl` is the only attribute whose stored value is not its
                        label: sizes are indexed in ml and shown in cl. `distilledYear` needs no
                        case — the indexer emits a bare 4-digit year. */}
                    {attributeName === 'bottleSizeInMl'
                      ? `${Number(item.label) / 10}cl`
                      : item.label}{' '}
                    <span className="tw-rounded-full tw-bg-baby-blue tw-px-3 tw-py-1 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-text-textBlack">
                      {item.count}
                    </span>
                  </Label>
                </Field>
              ))}
            </ul>
          </div>
        </div>
      </Transition>
    </li>
  );
}
